import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';
import { AlarmPriority } from 'shared/components/alarm-card/enums/alarm-priority';
import { AlarmStatus } from 'shared/components/alarm-card/enums/alarm-status';
import getPriorityColor from 'shared/components/alarm-card/utils/get-priority-color';
import AlarmIcon from 'shared/components/icons/alarm-icon';
import { getAlarmTimeForAlarmCard } from 'shared/utils/date-utils';

import { AlarmCardProps, useAlarmCard } from './hooks';

import '../alarm-card.scss';

type Props = AlarmCardProps & {
  priority: AlarmPriority;
  status: AlarmStatus;
  translatedType: string;
  isDiverted?: boolean;
  nearbyText?: string;
  message?: string;
  statusLastChangedAt?: string;
  statusLastChangedByFullName: string;
  statusLastChangedByEmail: string;
  isResidentialLocation: boolean;
};

const AlarmCard = ({
  isDiverted,
  priority,
  status,
  type,
  translatedType,
  nearbyText,
  message,
  statusLastChangedAt,
  statusLastChangedByFullName,
  statusLastChangedByEmail,
  isResidentialLocation,
  isAssistanceType,
  reporterIdentityType,
  reportedByFullName,
  organisationUnitName,
  descendantOrganisationUnitNames,
}: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('alarmCard');
  const { isGenericOrEmergency, getOrganisationUnitPrefix, getOrganisationUnitName } = useAlarmCard({
    type,
    isAssistanceType,
    reporterIdentityType,
    reportedByFullName,
    organisationUnitName,
    descendantOrganisationUnitNames,
  });

  return (
    <div className={`alarm-card-container ${getPriorityColor(priority)} status-${status}`}>
      {!!isDiverted && (
        <div className="diverted-tag">
          <Typography variant="overline">{t('divertedAlarm')}</Typography>
        </div>
      )}
      <div className="alarm-card-content">
        <div className={`content-names-row status-${status}`}>
          <div className="notification-icon-background">
            <AlarmIcon type={type} />
          </div>
          <div className="content-names">
            {(isResidentialLocation || isAssistanceType) && <Typography variant="body1">{getOrganisationUnitPrefix(t)}</Typography>}
            <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
              {getOrganisationUnitName()}
            </Typography>
          </div>
        </div>
        <div className={`content-information-row status-${status}`}>
          <Typography variant="subtitle1">{isGenericOrEmergency ? message : translatedType}</Typography>
          <Typography variant="subtitle1">{getAlarmTimeForAlarmCard(statusLastChangedAt, t('dateDisplayYesterday'))}</Typography>
        </div>

        {message && !isGenericOrEmergency && (
          <div className={`content-information-row status-${status}`}>
            <Typography variant="subtitle1">{message}</Typography>
          </div>
        )}

        {nearbyText && (
          <div className={`content-nearby-information-row status-${status}`}>
            <Typography variant="subtitle1">{t('nearby')}</Typography>
            <Typography variant="body1">{nearbyText}</Typography>
          </div>
        )}

        {status !== AlarmStatus.Reported && (
          <div className="status-container">
            {statusLastChangedByFullName && statusLastChangedByEmail && (
              <div className="avatar-container">
                <Typography variant="overline" sx={{ color: Constants.Colors.onSurfaceVariant }} className="name">
                  {`${statusLastChangedByFullName?.split(' ').shift()?.charAt(0)}${statusLastChangedByFullName
                    ?.split(' ')
                    .pop()
                    ?.charAt(0)}`}
                </Typography>
              </div>
            )}
            <Typography variant="overline" sx={{ color: Constants.Colors.onSurfaceVariant }} className="name">
              {t(status)} {statusLastChangedByFullName && statusLastChangedByEmail ? `${t('by')} ${statusLastChangedByFullName}` : ''}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlarmCard;
